/**
 * Owner Tools Page
 * Exclusive tools for the owner (Signature tier) to manage and test the app
 * - Tier Preview Mode: View app as different subscription tiers
 * - Test Account Creator: Create temporary accounts for beta testing
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { 
  Eye, Users, UserPlus, Trash2, Crown, Shield, Settings, 
  Calendar, Clock, Mail, Lock, RefreshCw, Copy, Check,
  Zap, Star, Building2, FlaskConical, Download, Printer, FileText, RotateCcw, DollarSign, AlertTriangle, ExternalLink
} from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import { getRoleDisplayName } from "@/utils/roleDisplayNames";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier configuration
const TIERS = [
  { id: 'basic', name: 'Basic', icon: Star, color: 'amber', price: '$29/mo', children: '25 children' },
  { id: 'premium', name: 'Premium', icon: Zap, color: 'blue', price: '$49/mo', children: '50 children' },
  { id: 'deluxe', name: 'Premium Plus', icon: Crown, color: 'purple', price: '$79/mo', children: '90 children' },
  { id: 'enterprise', name: 'Enterprise', icon: Building2, color: 'emerald', price: '$99/mo', children: '125 children' },
  { id: 'expired', name: 'Expired (Testing)', icon: Lock, color: 'red', price: 'Locked', children: 'View-only mode' }
];

const OWNER_EMAILS = ['todayskidz@comcast.net', 'todayskidz45@gmail.com'];

// Helper components for permission tables
const PermissionCheck = () => <span className="text-emerald-600 font-bold text-base">✓</span>;
const PermissionCross = () => <span className="text-rose-400 font-bold text-base">✕</span>;

const ROLES = [
  { key: 'owner', label: 'Director' },
  { key: 'co-owner', label: 'Supervisor/Admin' },
  { key: 'business-manager', label: 'Business Manager' },
  { key: 'staff', label: 'Staff' },
];

const PERMISSION_FEATURES = [
  { key: "home_dashboard",            label: "Home Dashboard",                    section: "General" },
  { key: "manage_children",           label: "Enrolled Children",                   section: "Children" },
  { key: "bulk_upload_children",      label: "Bulk Upload (Children)",            section: "Children" },
  { key: "child_records_docs",        label: "Compliance Records",              section: "Children" },
  { key: "records_bulk_upload",       label: "Compliance Records Bulk Date Entry",               section: "Children" },
  { key: "enrollment_packet",         label: "Enrollment Package",              section: "Children" },
  { key: "attendance_report",         label: "Attendance Report",                 section: "Children" },
  { key: "daily_reports",             label: "Daily Reports (Infant/Toddler)",    section: "Children" },
  { key: "emergency_contact",         label: "Emergency Contact",                 section: "Children" },
  { key: "field_trip_tags",           label: "Field Trip Tags",                   section: "Children" },
  { key: "staff_compliance_records",  label: "Staff Compliance Records",                    section: "Staff" },
  { key: "staff_clearance_hub",       label: "Staff Clearance Hub",                           section: "Staff" },
  { key: "professional_development",  label: "Professional Development Hub",                  section: "Staff" },
  { key: "manage_staff",              label: "Manage Staff",                                  section: "Staff" },
  { key: "staff_assignment",          label: "Daily Roster",                                  section: "Staff" },
  { key: "roster_view_all_staff",     label: "View All Staff Names (On Daily Roster)",         section: "Staff" },
  { key: "staff_evaluation",          label: "Staff Evaluation",                              section: "Staff" },
  { key: "staff_upload_center",       label: "Staff Upload Center",                           section: "Staff" },
  { key: "lesson_planning",           label: "Lesson Planning Hub",               section: "Learning" },
  { key: "compliance_dashboard",      label: "Compliance Dashboard",              section: "Compliance" },
  { key: "inspection_binder",         label: "Inspection Binder",                 section: "Compliance" },
  { key: "attendance_trends",         label: "Attendance Trends & Analytics",     section: "Compliance" },
  { key: "backup_restore",            label: "Backup & Restore",                  section: "Settings" },
  { key: "policy_acknowledgements",   label: "Policy Acknowledgements",           section: "Settings" },
  { key: "security",                  label: "Security Settings",                 section: "Settings" },
  { key: "email_activity",            label: "Email Activity",                    section: "Settings" },
  { key: "payments",                  label: "Payments",                          section: "Billing" },
  { key: "auto_invoicing",            label: "Auto Invoicing",                    section: "Billing" },
  { key: "payroll_hub",               label: "Payroll Hub",                       section: "Billing" },
  { key: "pickup_safety_alerts",      label: "Pick Up Safety Alerts",             section: "Revenue" },
  { key: "parent_sms_communications", label: "Parent SMS Communications",         section: "Revenue" },
  { key: "parent_sms_billing",         label: "Parent SMS Subscription & Billing",  section: "Revenue" },
  { key: "family_communications_hub",  label: "Family Communications Hub",          section: "Revenue" },
  { key: "family_sms_portal",          label: "Family SMS Access Portal",            section: "Revenue" },
  { key: "platform_revenue",           label: "Platform Revenue (Emergent owner)",    section: "Revenue" },
  { key: "parent_communication_email", label: "Parent Communication Center Email",   section: "Revenue" },
  { key: "enrollment_forms",           label: "Enrollment Forms",                    section: "Revenue" },
  { key: "form_builder_hub",           label: "Form Builder Hub (sidebar)",          section: "Revenue" },
  { key: "meal_compliance_tracker",   label: "Meal Compliance Tracker",           section: "General" },
  { key: "center_menu",               label: "Center Menu",                       section: "General" },
  { key: "bus_tracking",              label: "Bus Tracking",                      section: "General" },
  { key: "cacfp_meal_benefits",       label: "CACFP Meal Benefits",               section: "General" },
];

// Roles that default to UNCHECKED for a feature (still toggleable — checking saves an override)
const PERMISSION_DEFAULT_OFF = {
  form_builder_hub:     ['business-manager', 'staff'],
  email_activity:       ['co-owner', 'business-manager', 'staff'],
};

const OwnerToolsPage = () => {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const [loading, setLoading] = useState(false);

  // ── Admin Support Access Log ──
  const [adminAccessLog, setAdminAccessLog] = useState([]);
  const [adminLogLoading, setAdminLogLoading] = useState(false);
  const loadAdminAccessLog = async () => {
    setAdminLogLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/access-log`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminAccessLog(res.data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load access log');
    } finally {
      setAdminLogLoading(false);
    }
  };
  useEffect(() => { loadAdminAccessLog(); }, []);

  // ── User Cleanup (duplicate parent users) ──
  const [cleanupEmail, setCleanupEmail] = useState("");
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Orphan Parent Access Cleanup (portal privacy) ──
  const [orphanCleanupLoading, setOrphanCleanupLoading] = useState(false);
  const [orphanCleanupResult, setOrphanCleanupResult] = useState(null);

  const runOrphanCleanup = async () => {
    setOrphanCleanupLoading(true);
    setOrphanCleanupResult(null);
    try {
      const res = await axios.post(`${API}/parent-communication/cleanup-orphan-access`);
      setOrphanCleanupResult(res.data);
      toast.success(`Cleaned ${res.data.recordsCleaned} record(s), removed ${res.data.orphanChildIdsRemoved} orphan link(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setOrphanCleanupLoading(false);
    }
  };

  const inspectParentUsers = async () => {
    const target = (cleanupEmail || "").trim().toLowerCase();
    if (!target) { toast.error("Enter an email"); return; }
    setCleanupLoading(true);
    setCleanupResult(null);
    try {
      const res = await axios.get(`${API}/owner/inspect-parent-users`, { params: { email: target } });
      setCleanupResult(res.data);
      toast.success(`Found ${res.data.userCount} user record(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setCleanupLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!pendingDelete) return;
    try {
      // Combo flow: if the user is "Linked" (has portal access rows), use the combo endpoint
      // to delete both atomically. Otherwise the simple orphan-deletion endpoint.
      const url = pendingDelete.isLinkedToPortalAccess
        ? `${API}/owner/cleanup-parent-portal-and-user`
        : `${API}/owner/cleanup-duplicate-parent-user`;
      await axios.post(url, {
        email: cleanupResult?.email,
        userIdToDelete: pendingDelete.id,
      });
      toast.success(`Deleted ${pendingDelete.id.slice(0, 8)}… (audit logged)`);
      setPendingDelete(null);
      // refresh
      await inspectParentUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Staff Tier Assignment
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffTierDialogOpen, setStaffTierDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedTier, setSelectedTier] = useState('basic');
  const [selectedRole, setSelectedRole] = useState('owner');
  
  // Test Account Creator
  const [testAccounts, setTestAccounts] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [testAccountForm, setTestAccountForm] = useState({
    email: '',
    password: '',
    organizationName: '',
    tier: 'enterprise',
    expirationDays: 14,
    expirationDate: '',
    useSpecificDate: false
  });
  const [copiedId, setCopiedId] = useState(null);
  const [subReport, setSubReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);

  // Permissions Manager state
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsDirty, setPermissionsDirty] = useState(false);

  // Tier Permissions Manager state
  const [tierPerms, setTierPerms] = useState({});
  const [tierPermsLoading, setTierPermsLoading] = useState(false);
  const [tierPermsDirty, setTierPermsDirty] = useState(false);

  // Pricing Manager state
  const [pricing, setPricing] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingDirty, setPricingDirty] = useState(false);
  const [pricingEdits, setPricingEdits] = useState({});
  const [savedPricingKeys, setSavedPricingKeys] = useState([]);

  // Check if current user is owner
  const isOwner = OWNER_EMAILS.includes(user?.email?.toLowerCase());

  useEffect(() => {
    if (!isOwner) {
      toast.error("Only the owner can access this page");
      navigate("/");
      return;
    }
    fetchStaffUsers();
    fetchTestAccounts();
    fetchPermissions();
    fetchTierPermissions();
    fetchPricing();
  }, [user, navigate]);

  // ============== STAFF TIER MANAGEMENT ==============
  
  const fetchStaffUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setStaffUsers(response.data.filter(u => u.role !== "parent"));
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const updateStaffTier = async () => {
    if (!selectedStaff) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/owner/staff-tier`, {
        userId: selectedStaff.id,
        tier: selectedTier,
        role: selectedRole
      });
      toast.success(`${selectedStaff.name}'s tier updated to ${selectedTier} with ${selectedRole} role`);
      setStaffTierDialogOpen(false);
      fetchStaffUsers();
    } catch (error) {
      console.error("Error updating tier:", error);
      toast.error("Failed to update tier");
    } finally {
      setLoading(false);
    }
  };

  // ============== TEST ACCOUNT MANAGEMENT ==============
  
  const fetchTestAccounts = async () => {
    try {
      const response = await axios.get(`${API}/owner/test-accounts`);
      setTestAccounts(response.data);
    } catch (error) {
      console.error("Error fetching test accounts:", error);
    }
  };

  const createTestAccount = async () => {
    if (!testAccountForm.email || !testAccountForm.password || !testAccountForm.organizationName) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: testAccountForm.email,
        password: testAccountForm.password,
        organizationName: testAccountForm.organizationName,
        tier: testAccountForm.tier,
        expirationDays: testAccountForm.useSpecificDate ? null : parseInt(testAccountForm.expirationDays),
        expirationDate: testAccountForm.useSpecificDate ? testAccountForm.expirationDate : null
      };

      await axios.post(`${API}/owner/test-accounts`, payload);
      toast.success("Test account created successfully!");
      setCreateDialogOpen(false);
      setTestAccountForm({
        email: '',
        password: '',
        organizationName: '',
        tier: 'enterprise',
        expirationDays: 14,
        expirationDate: '',
        useSpecificDate: false
      });
      fetchTestAccounts();
    } catch (error) {
      console.error("Error creating test account:", error);
      toast.error(error.response?.data?.detail || "Failed to create test account");
    } finally {
      setLoading(false);
    }
  };

  const deleteTestAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to delete this test account?")) return;
    
    try {
      await axios.delete(`${API}/owner/test-accounts/${accountId}`);
      toast.success("Test account deleted");
      fetchTestAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete test account");
    }
  };

  const copyCredentials = (account) => {
    const text = `Email: ${account.email}\nPassword: ${account.password}\nOrganization: ${account.organizationName}\nTier: ${account.tier}\nExpires: ${new Date(account.expiresAt).toLocaleDateString()}`;
    navigator.clipboard.writeText(text);
    setCopiedId(account.id);
    toast.success("Credentials copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ============== PERMISSIONS MANAGER ==============

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/owner/permissions`);
      setPermissions(res.data.permissions || {});
      setPermissionsDirty(false);
    } catch { toast.error("Failed to load permissions"); }
  };

  const togglePermission = (featureKey, roleKey) => {
    if (roleKey === 'owner') return; // Director always has all access
    setPermissions(prev => ({
      ...prev,
      [featureKey]: { ...(prev[featureKey] || {}), [roleKey]: !(prev[featureKey]?.[roleKey] ?? true) }
    }));
    setPermissionsDirty(true);
  };

  const savePermissions = async () => {
    setPermissionsLoading(true);
    try {
      await axios.put(`${API}/owner/permissions`, { permissions });
      toast.success("Permissions saved");
      setPermissionsDirty(false);
    } catch { toast.error("Failed to save permissions"); }
    finally { setPermissionsLoading(false); }
  };

  const resetPermissions = async () => {
    if (!window.confirm("Reset all permissions to defaults?")) return;
    setPermissionsLoading(true);
    try {
      const res = await axios.delete(`${API}/owner/permissions`);
      setPermissions(res.data.permissions || {});
      setPermissionsDirty(false);
      toast.success("Permissions reset to defaults");
    } catch { toast.error("Failed to reset permissions"); }
    finally { setPermissionsLoading(false); }
  };

  const printPermissions = () => window.print();

  // ============== TIER PERMISSIONS MANAGER ==============

  const fetchTierPermissions = async () => {
    try {
      const res = await axios.get(`${API}/owner/tier-permissions`);
      setTierPerms(res.data.tiers || {});
      setTierPermsDirty(false);
    } catch { toast.error("Failed to load tier permissions"); }
  };

  const saveTierPermissions = async () => {
    setTierPermsLoading(true);
    try {
      await axios.put(`${API}/owner/tier-permissions`, { tiers: tierPerms });
      toast.success("Tier permissions saved");
      setTierPermsDirty(false);
    } catch { toast.error("Failed to save tier permissions"); }
    finally { setTierPermsLoading(false); }
  };

  const resetTierPermissions = async () => {
    if (!window.confirm("Reset all tier permissions to defaults?")) return;
    setTierPermsLoading(true);
    try {
      const res = await axios.delete(`${API}/owner/tier-permissions`);
      setTierPerms(res.data.tiers || {});
      setTierPermsDirty(false);
      toast.success("Tier permissions reset to defaults");
    } catch { toast.error("Failed to reset tier permissions"); }
    finally { setTierPermsLoading(false); }
  };

  // ============== PRICING MANAGER ==============

  const fetchPricing = async () => {
    setPricingLoading(true);
    try {
      const res = await axios.get(`${API}/owner/pricing`);
      setPricing(res.data.pricing || {});
      setPricingEdits(Object.fromEntries(Object.entries(res.data.pricing || {}).map(([k, v]) => [k, v.price])));
    } catch { toast.error("Failed to load pricing"); }
    finally { setPricingLoading(false); }
  };

  const savePricing = async () => {
    setPricingLoading(true);
    try {
      const updated = Object.fromEntries(Object.entries(pricingEdits).map(([k, v]) => [k, { price: parseFloat(v) || 0 }]));
      const res = await axios.put(`${API}/owner/pricing`, { pricing: updated });
      setPricing(res.data.pricing || {});
      setSavedPricingKeys(Object.keys(pricingEdits).filter(k => parseFloat(pricingEdits[k]) !== pricing[k]?.price));
      setPricingDirty(false);
      toast.success("Pricing saved — update your Square items to match");
    } catch { toast.error("Failed to save pricing"); }
    finally { setPricingLoading(false); }
  };

  // ============== SUBSCRIPTION REPORT ==============

  const fetchSubReport = async () => {    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/owner/parent-comm-report`);
      setSubReport(res.data.report || []);
    } catch {
      toast.error("Failed to load subscription report");
    } finally {
      setReportLoading(false);
    }
  };

  const downloadCSV = () => {
    const headers = ["Center Name", "Owner Email", "Plan", "Status", "Subscribed Date", "End Date", "Square ID"];
    const rows = subReport.map(r => [
      r.centerName,
      r.ownerEmail,
      r.billingCycle,
      r.status,
      r.subscribedAt ? new Date(r.subscribedAt).toLocaleDateString() : "—",
      r.endDate ? new Date(r.endDate).toLocaleDateString() : "—",
      r.squarePaymentId,
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `parent-comm-subscriptions-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'basic': return 'bg-amber-100 text-amber-800';
      case 'premium': return 'bg-blue-100 text-blue-800';
      case 'deluxe': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-emerald-100 text-emerald-800';
      case 'signature': return 'bg-gradient-to-r from-pink-500 to-violet-500 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ============== RENDER ==============
  
  if (!isOwner) return null;

  return (
    <AppShell pageTitle="Owner Tools" breadcrumb>
      {/* Hero Banner - Full display */}
      <PageHero 
        imageUrl={HERO_IMAGES.settings}
        title="Owner Tools"
        subtitle="Exclusive tools to manage tiers and create test accounts"
        altText="Owner administration tools"
      />

      <div data-testid="owner-tools-page" className="space-y-6 mt-6">
        <Tabs defaultValue="staff-tiers" className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 h-auto w-full justify-between bg-white p-2 rounded-xl shadow-card border border-line">
            <TabsTrigger value="staff-tiers" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white rounded-lg">
              <Users className="w-4 h-4 mr-1" />
              Staff Tiers
            </TabsTrigger>
            {/* Hidden — Test Accounts consolidated to Admin Customers page.
                Kept for reversibility per code-safety policy.
            <TabsTrigger value="test-accounts" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white rounded-lg">
              <FlaskConical className="w-4 h-4 mr-1" />
              Test Accounts
            </TabsTrigger>
            */}
            <TabsTrigger value="access-reference" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white rounded-lg">
              <Shield className="w-4 h-4 mr-1" />
              Access Ref
            </TabsTrigger>
            <TabsTrigger value="permissions" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
              <Lock className="w-4 h-4 mr-1" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="tier-permissions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white rounded-lg">
              <Star className="w-4 h-4 mr-1" />
              Tier Permissions
            </TabsTrigger>
            <TabsTrigger value="user-cleanup" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white rounded-lg" data-testid="tab-user-cleanup">
              <Trash2 className="w-4 h-4 mr-1" />
              User Cleanup
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg" data-testid="tab-pricing">
              <DollarSign className="w-4 h-4 mr-1" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="admin-access" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-lg" data-testid="tab-admin-access">
              <Shield className="w-4 h-4 mr-1" />
              Admin Support Access Log
            </TabsTrigger>
          </TabsList>

          {/* Staff Tier Assignment Tab */}
          <TabsContent value="staff-tiers">
            <Card className="shadow-card border border-line">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-violet-500" />
                  Staff Tier Assignment
                </CardTitle>
                <CardDescription>
                  Assign different tiers to staff accounts to test what customers see at each level
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {staffUsers.length === 0 ? (
                    <p className="text-ink-muted text-center py-8">No staff members found</p>
                  ) : (
                    <div className="divide-y">
                      {staffUsers.map((staff) => (
                        <div key={staff.id} className="flex items-center justify-between py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              staff.role === 'owner' ? 'bg-pink-100' : 
                              staff.role === 'co-owner' ? 'bg-violet-100' : 'bg-gray-100'
                            }`}>
                              {staff.role === 'owner' ? <Crown className="w-5 h-5 text-pink-600" /> :
                               staff.role === 'co-owner' ? <Shield className="w-5 h-5 text-violet-600" /> :
                               <Users className="w-5 h-5 text-gray-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-ink-main">{staff.name}</p>
                              <p className="text-sm text-ink-muted">{staff.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={getTierBadgeColor(staff.viewAsTier || 'signature')}>
                              {staff.viewAsTier 
                                ? `${staff.viewAsTier.charAt(0).toUpperCase() + staff.viewAsTier.slice(1)} (Test)`
                                : 'Signature'}
                            </Badge>
                            {(staff.viewAsTier || (!staff.email?.toLowerCase().includes('todayskidz@comcast.net') && staff.role !== 'owner')) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedStaff(staff);
                                  setSelectedTier(staff.viewAsTier || 'basic');
                                  setSelectedRole(staff.viewAsTier ? staff.role : 'owner');
                                  setStaffTierDialogOpen(true);
                                }}
                              >
                                {staff.viewAsTier ? 'Change Tier' : 'Set Test Tier'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Account Creator Tab */}
          <TabsContent value="test-accounts">
            <Card className="shadow-card border border-line">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FlaskConical className="w-5 h-5 text-emerald-500" />
                      Test Account Creator
                    </CardTitle>
                    <CardDescription>
                      Create temporary test accounts for childcare centers to beta test your app
                    </CardDescription>
                  </div>
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Test Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create Test Account</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Organization Name *</Label>
                          <Input
                            placeholder="Test Childcare Center"
                            value={testAccountForm.organizationName}
                            onChange={(e) => setTestAccountForm({...testAccountForm, organizationName: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email Address *</Label>
                          <Input
                            type="email"
                            placeholder="test@example.com"
                            value={testAccountForm.email}
                            onChange={(e) => setTestAccountForm({...testAccountForm, email: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password *</Label>
                          <Input
                            type="text"
                            placeholder="Enter password"
                            value={testAccountForm.password}
                            onChange={(e) => setTestAccountForm({...testAccountForm, password: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Subscription Tier</Label>
                          <Select 
                            value={testAccountForm.tier}
                            onValueChange={(value) => setTestAccountForm({...testAccountForm, tier: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIERS.map((tier) => (
                                <SelectItem key={tier.id} value={tier.id}>
                                  <span className="flex items-center gap-2">
                                    <tier.icon className="w-4 h-4" />
                                    {tier.name} ({tier.price})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Expiration</Label>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-ink-muted">Days</span>
                              <Switch
                                checked={testAccountForm.useSpecificDate}
                                onCheckedChange={(checked) => setTestAccountForm({...testAccountForm, useSpecificDate: checked})}
                              />
                              <span className="text-sm text-ink-muted">Date</span>
                            </div>
                          </div>
                          {testAccountForm.useSpecificDate ? (
                            <Input
                              type="date"
                              value={testAccountForm.expirationDate}
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => setTestAccountForm({...testAccountForm, expirationDate: e.target.value})}
                            />
                          ) : (
                            <Select 
                              value={testAccountForm.expirationDays.toString()}
                              onValueChange={(value) => setTestAccountForm({...testAccountForm, expirationDays: parseInt(value)})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="7">7 days</SelectItem>
                                <SelectItem value="14">14 days</SelectItem>
                                <SelectItem value="30">30 days</SelectItem>
                                <SelectItem value="60">60 days</SelectItem>
                                <SelectItem value="90">90 days</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={createTestAccount} 
                          disabled={loading}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          {loading ? "Creating..." : "Create Account"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {testAccounts.length === 0 ? (
                  <div className="text-center py-12">
                    <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-ink-muted">No test accounts created yet</p>
                    <p className="text-sm text-ink-muted mt-1">Create test accounts to send to childcare centers for beta testing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {testAccounts.map((account) => {
                      const isExpired = new Date(account.expiresAt) < new Date();
                      return (
                        <div key={account.id} className={`border rounded-xl p-4 ${isExpired ? 'bg-red-50 border-red-200' : 'bg-white border-line'}`}>
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-ink-main">{account.organizationName}</h4>
                                <Badge className={getTierBadgeColor(account.tier)}>
                                  {account.tier.charAt(0).toUpperCase() + account.tier.slice(1)}
                                </Badge>
                                {isExpired && <Badge variant="destructive">Expired</Badge>}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-ink-muted">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" />
                                  {account.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Lock className="w-4 h-4" />
                                  {account.password}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-ink-muted">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  Expires: {new Date(account.expiresAt).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  Created: {new Date(account.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyCredentials(account)}
                              >
                                {copiedId === account.id ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => deleteTestAccount(account.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Access Reference Tab */}
          <TabsContent value="access-reference">
            <Card className="shadow-card border border-line">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-600" />
                      Role Access Reference
                    </CardTitle>
                    <CardDescription>What each role can access based on tier. Director = Owner · Supervisor/Admin = Co-Owner</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(() => {
                  const rows = [
                    // Feature, Tier, Staff, Director(owner), Supervisor/Admin(co-owner)
                    { feature: "Home Dashboard", section: "General", tier: "All", staff: true, director: true, supervisor: true },
                    { feature: "Enrolled Children", section: "Children", tier: "Basic", staff: true, director: true, supervisor: true },
                    { feature: "Emergency Contact", section: "Children", tier: "Basic", staff: true, director: true, supervisor: true },
                    { feature: "Field Trip Tags", section: "Children", tier: "Basic", staff: true, director: true, supervisor: true },
                    { feature: "Daily Reports (Infant/Toddler)", section: "Children", tier: "Premium", staff: true, director: true, supervisor: true },
                    { feature: "Bulk Upload", section: "Children", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Compliance Records", section: "Children", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Attendance Report", section: "Children", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Staff Compliance Records", section: "Staff", tier: "Premium", staff: true, director: true, supervisor: true },
                    { feature: "Staff Assignment", section: "Staff", tier: "Premium", staff: true, director: true, supervisor: true },
                    { feature: "Manage Staff", section: "Staff", tier: "Premium Plus", staff: false, director: true, supervisor: false },
                    { feature: "Attendance Trends & Analytics", section: "Compliance", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Compliance Dashboard", section: "Compliance", tier: "Premium Plus", staff: false, director: true, supervisor: true },
                    { feature: "Inspection Binder", section: "Compliance", tier: "Premium Plus", staff: false, director: true, supervisor: true },
                    { feature: "Meal Compliance Tracker", section: "General", tier: "Premium Plus", staff: true, director: true, supervisor: true },
                    { feature: "Center Menu", section: "General", tier: "Premium", staff: true, director: true, supervisor: true },
                    { feature: "Pick Up Safety Alerts", section: "Settings", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Parent Portal", section: "Settings", tier: "Premium", staff: false, director: true, supervisor: true },
                    { feature: "Backup & Restore", section: "Settings", tier: "Premium", staff: true, director: true, supervisor: true },
                    { feature: "Security", section: "Settings", tier: "Enterprise", staff: true, director: true, supervisor: true },
                    { feature: "Payments", section: "Billing", tier: "Enterprise", staff: false, director: true, supervisor: true },
                    { feature: "Auto Invoicing", section: "Billing", tier: "Enterprise", staff: false, director: true, supervisor: true },
                    { feature: "Payroll Hub", section: "Billing", tier: "Enterprise", staff: false, director: true, supervisor: true },
                    { feature: "Center Communications", section: "Home", tier: "Premium+", staff: true, director: true, supervisor: true },
                    { feature: "Parent Communications (SMS)", section: "Home", tier: "Premium+", staff: true, director: true, supervisor: true },
                  ];

                  const tierColor = { All: "bg-slate-100 text-slate-600", Basic: "bg-green-100 text-green-700", Premium: "bg-blue-100 text-blue-700", "Premium+": "bg-blue-100 text-blue-700", Deluxe: "bg-purple-100 text-purple-700", Enterprise: "bg-amber-100 text-amber-700" };

                  return (
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Feature</th>
                          <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Section</th>
                          <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Min Tier</th>
                          <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-center text-slate-500">Staff</th>
                          <th className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-center text-slate-500">Director</th>
                          <th className="pb-3 text-xs font-semibold uppercase tracking-wide text-center text-slate-500">Supervisor/Admin</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="py-2.5 pr-4 font-medium text-slate-800">{row.feature}</td>
                            <td className="py-2.5 pr-4 text-slate-500 text-xs">{row.section}</td>
                            <td className="py-2.5 pr-4">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tierColor[row.tier] || 'bg-slate-100 text-slate-600'}`}>{row.tier}</span>
                            </td>
                            <td className="py-2.5 pr-4 text-center">{row.staff ? <PermissionCheck /> : <PermissionCross />}</td>
                            <td className="py-2.5 pr-4 text-center">{row.director ? <PermissionCheck /> : <PermissionCross />}</td>
                            <td className="py-2.5 text-center">{row.supervisor ? <PermissionCheck /> : <PermissionCross />}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Permissions Manager Tab */}
          <TabsContent value="permissions">
            <Card className="shadow-card border border-line">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="w-5 h-5 text-blue-600" />
                      Permissions Manager
                    </CardTitle>
                    <CardDescription>Control which roles can access each feature. Director always has full access.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {permissionsDirty && (
                      <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                    )}
                    <Button variant="outline" size="sm" onClick={resetPermissions} disabled={permissionsLoading}>
                      <RotateCcw className="w-4 h-4 mr-2" /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={printPermissions}>
                      <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    <Button size="sm" onClick={savePermissions} disabled={permissionsLoading || !permissionsDirty}
                      className="bg-blue-600 hover:bg-blue-700 text-white">
                      {permissionsLoading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(() => {
                  const sections = [...new Set(PERMISSION_FEATURES.map(f => f.section))];
                  return (
                    <table className="w-full text-sm min-w-[700px]">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-48">Feature</th>
                          <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-24">Section</th>
                          {ROLES.map(role => (
                            <th key={role.key} className="pb-3 px-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 w-32">
                              {role.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sections.map(section => (
                          <>
                            <tr key={`section-${section}`} className="bg-slate-50">
                              <td colSpan={6} className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-slate-400">{section}</td>
                            </tr>
                            {PERMISSION_FEATURES.filter(f => f.section === section).map(feature => (
                              <tr key={feature.key} className="border-b border-slate-100 hover:bg-blue-50/30">
                                <td className="py-2.5 pr-4 font-medium text-slate-800">{feature.label}</td>
                                <td className="py-2.5 pr-4"></td>
                                {ROLES.map(role => {
                                  const isOwnerRole = role.key === 'owner';
                                  const defaultOff = PERMISSION_DEFAULT_OFF[feature.key]?.includes(role.key);
                                  const checked = isOwnerRole ? true : (permissions[feature.key]?.[role.key] ?? !defaultOff);
                                  return (
                                    <td key={role.key} className="py-2.5 px-3 text-center">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={isOwnerRole}
                                        onChange={() => togglePermission(feature.key, role.key)}
                                        className="w-4 h-4 accent-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Tier Permissions Tab */}
          <TabsContent value="tier-permissions">
            <Card className="shadow-card border border-line">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" />
                      Tier Permissions
                    </CardTitle>
                    <CardDescription>See which features are available at each subscription tier and change the minimum tier required per feature.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {tierPermsDirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
                    <Button variant="outline" size="sm" onClick={resetTierPermissions} disabled={tierPermsLoading}>
                      <RotateCcw className="w-4 h-4 mr-2" /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.print()}>
                      <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    <Button size="sm" onClick={saveTierPermissions} disabled={tierPermsLoading || !tierPermsDirty}
                      className="bg-amber-500 hover:bg-amber-600 text-white">
                      {tierPermsLoading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(() => {
                  const TIERS_LIST = [
                    { key: "basic",      label: "Basic",         color: "bg-amber-100 text-amber-800" },
                    { key: "premium",    label: "Premium",       color: "bg-blue-100 text-blue-800" },
                    { key: "deluxe",     label: "Premium Plus",  color: "bg-purple-100 text-purple-800" },
                    { key: "enterprise", label: "Enterprise",    color: "bg-emerald-100 text-emerald-800" },
                    { key: "signature",  label: "Signature",     color: "bg-pink-100 text-pink-800" },
                  ];
                  const TIER_ORDER_LIST = ["basic", "premium", "deluxe", "enterprise", "signature"];

                  const ALL_FEATURES = [
                    { key: "daily_signin",             label: "Daily Sign-In/Out",                       section: "Basic" },
                    { key: "child_management",         label: "Child Management",                        section: "Basic" },
                    { key: "emergency_report",         label: "Emergency Contact Report",                section: "Basic" },
                    { key: "attendance_summary",       label: "Attendance Summary",                      section: "Basic" },
                    { key: "printing_download",        label: "Printing & Download",                     section: "Basic" },
                    { key: "holiday_calendar",         label: "Holiday & Events Calendar",               section: "Basic" },
                    { key: "smart_filters",            label: "Smart Filters",                           section: "Basic" },
                    { key: "school_calendar_sync",     label: "School Calendar Sync",                    section: "Basic" },
                    { key: "feedback",                 label: "Feedback & Suggestions",                  section: "Basic" },
                    { key: "menu_planner",             label: "12 Month Menu Planner",                   section: "Premium" },
                    { key: "bus_tracking",             label: "A.M. Bus Tracking",                       section: "Premium" },
                    { key: "center_alerts",            label: "Real Time Center Alerts",                 section: "Premium" },
                    { key: "voice_input",              label: "Voice Input",                             section: "Premium" },
                    { key: "bulk_upload",              label: "Bulk Upload / Delete",                    section: "Premium" },
                    { key: "attendance_trends",        label: "Attendance Trends",                       section: "Premium" },
                    { key: "child_compliance_uploads", label: "Child Compliance Uploads / Enrollment Package", section: "Premium" },
                    { key: "attendance_reports",       label: "Attendance Reports",                      section: "Premium" },
                    { key: "staff_calendar",           label: "Staff Calendar",                          section: "Premium" },
                    { key: "pickup_safety_email",      label: "Pickup Safety Alerts (Email)",            section: "Premium" },
                    { key: "parent_sms_communications",  label: "Parent SMS Communications",               section: "Premium" },
                    { key: "family_communications_hub",  label: "Family Communications Hub",               section: "Premium" },
                    { key: "parent_communication_email", label: "Parent Communication Center Email",        section: "Premium" },
                    { key: "parent_portal",              label: "Family SMS Access Portal",                 section: "Premium" },
                    { key: "enrollment_forms",           label: "Enrollment Forms",                         section: "Premium" },
                    { key: "form_builder_hub",           label: "Form Builder (sidebar)",                   section: "Premium" },
                    { key: "staff_compliance_tracker", label: "Staff & Child Compliance Tracker",        section: "Premium" },
                    { key: "infant_toddler_reports",   label: "Infant/Toddler Reports",                  section: "Premium" },
                    { key: "full_backup",              label: "Full Data Backup",                        section: "Premium" },
                    { key: "compliance_dashboard",     label: "Compliance Dashboard",                    section: "Premium Plus" },
                    { key: "inspection_mode",          label: "Inspection Mode",                         section: "Premium Plus" },
                    { key: "meal_compliance",          label: "Meal Compliance Tracker",                 section: "Premium Plus" },
                    { key: "inspection_binder",        label: "Inspection Binder (16 items)",            section: "Premium Plus" },
                    { key: "incident_reports",         label: "Incident Reports",                        section: "Premium Plus" },
                    { key: "user_management",          label: "User Management & Co-Owners",             section: "Enterprise" },
                    { key: "security_settings",        label: "Security Settings",                       section: "Enterprise" },
                    { key: "invoicing",                label: "Invoicing",                               section: "Enterprise" },
                    { key: "auto_invoicing",           label: "Auto-Invoicing",                          section: "Enterprise" },
                    { key: "payroll_hub",              label: "Payroll Hub",                             section: "Enterprise" },
                    { key: "admin_customers",          label: "Admin Customer View",                     section: "Signature" },
                    { key: "feedback_admin",           label: "Feedback Admin",                          section: "Signature" },
                    { key: "unlimited_children",       label: "Unlimited Children",                      section: "Signature" },
                  ];

                  const sections = [...new Set(ALL_FEATURES.map(f => f.section))];
                  const sectionColors = {
                    "Basic": "text-amber-700 bg-amber-50",
                    "Premium": "text-blue-700 bg-blue-50",
                    "Premium Plus": "text-purple-700 bg-purple-50",
                    "Enterprise": "text-emerald-700 bg-emerald-50",
                    "Signature": "text-pink-700 bg-pink-50",
                  };

                  return (
                    <table className="w-full text-sm min-w-[900px]">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="pb-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-56">Feature</th>
                          {TIERS_LIST.map(t => (
                            <th key={t.key} className="pb-3 px-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 w-24">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.color}`}>{t.label}</span>
                            </th>
                          ))}
                          <th className="pb-3 pl-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-36">Min Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sections.map(section => (
                          <>
                            <tr key={`section-${section}`}>
                              <td colSpan={7} className={`py-2 px-2 text-xs font-bold uppercase tracking-wider ${sectionColors[section] || 'text-slate-400 bg-slate-50'}`}>{section}</td>
                            </tr>
                            {ALL_FEATURES.filter(f => f.section === section).map(feature => {
                              const minTier = tierPerms[feature.key] || "basic";
                              const minIndex = TIER_ORDER_LIST.indexOf(minTier);
                              return (
                                <tr key={feature.key} className="border-b border-slate-100 hover:bg-amber-50/30">
                                  <td className="py-2.5 pr-4 font-medium text-slate-800">{feature.label}</td>
                                  {TIERS_LIST.map((t, i) => {
                                    const hasAccess = i >= minIndex;
                                    return (
                                      <td key={t.key} className="py-2.5 px-3 text-center">
                                        {hasAccess
                                          ? <span className="text-emerald-600 font-bold text-base">✓</span>
                                          : <span className="text-slate-300 text-base">—</span>
                                        }
                                      </td>
                                    );
                                  })}
                                  <td className="py-2.5 pl-4">
                                    <select
                                      value={minTier}
                                      onChange={e => {
                                        setTierPerms(prev => ({ ...prev, [feature.key]: e.target.value }));
                                        setTierPermsDirty(true);
                                      }}
                                      className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
                                    >
                                      {TIERS_LIST.map(t => (
                                        <option key={t.key} value={t.key}>{t.label}</option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Cleanup Tab — duplicate parent user diagnostic & cleanup (audit-logged) */}
          <TabsContent value="user-cleanup">
            {/* NEW: Portal Privacy Cleanup — orphan childId link scrubber */}
            <Card className="shadow-card border border-line mb-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  Portal Privacy Cleanup
                  <span
                    className="ml-1 text-[10px] font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 cursor-help"
                    title="Automatic data validation and one-click cleanup tools to ensure parent privacy and database accuracy."
                    data-testid="portal-privacy-tooltip"
                  >
                    ?
                  </span>
                </CardTitle>
                <CardDescription>
                  Scans every parent portal access record in your center and removes stale <code>childIds</code> that don&apos;t belong (child deleted, or parent no longer on the child&apos;s pickup list). Prevents cross-family data leaks.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={runOrphanCleanup}
                  disabled={orphanCleanupLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="orphan-cleanup-btn"
                >
                  {orphanCleanupLoading ? "Scanning…" : "Run Portal Privacy Cleanup"}
                </Button>
                {orphanCleanupResult && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1" data-testid="orphan-cleanup-result">
                    <p className="font-semibold text-emerald-800">Cleanup complete</p>
                    <p className="text-slate-700">Records scanned: <strong>{orphanCleanupResult.totalRecordsScanned}</strong></p>
                    <p className="text-slate-700">Records cleaned: <strong>{orphanCleanupResult.recordsCleaned}</strong></p>
                    <p className="text-slate-700">Orphan child links removed: <strong>{orphanCleanupResult.orphanChildIdsRemoved}</strong></p>
                    {orphanCleanupResult.details?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-slate-600 hover:text-slate-800">Show details</summary>
                        <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                          {orphanCleanupResult.details.map((d, i) => (
                            <div key={i} className="rounded border border-slate-200 bg-white p-2">
                              <p className="text-slate-700">Parent: <code>{d.parentEmail || "(no email)"}</code></p>
                              <p className="text-slate-500">Kept: {d.keptCount} child(ren)</p>
                              <ul className="list-disc list-inside text-rose-600">
                                {d.removed.map((r, ri) => (
                                  <li key={ri}>{r.childName || r.childId} — <em>{r.reason}</em></li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-card border border-line">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-rose-600" />
                  Duplicate Parent User Cleanup
                </CardTitle>
                <CardDescription>
                  Find and remove duplicate <strong>parent</strong> user records that share the same email.
                  Records linked to active portal access cannot be deleted. Every deletion is logged to <code>audit_user_deletions</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-2" data-testid="cleanup-instructions">
                  <p className="font-bold text-slate-700 uppercase tracking-wide text-[11px]">When to use this</p>
                  <p className="text-slate-600">
                    Use this tool when a parent invited via the <strong>Parent SMS / Family Access Portal</strong> reports they can&apos;t see their child&apos;s content (forms, menus, activity, etc.), even though they accepted their invite.
                    This usually means there are <strong>two user records</strong> with the same email — one orphaned from a prior invite — and the login is grabbing the wrong one.
                  </p>
                  <p className="font-bold text-slate-700 uppercase tracking-wide text-[11px] pt-1">Example</p>
                  <p className="text-slate-600">
                    Parent <code>parent@example.com</code> logs in but sees &quot;No forms available yet&quot;. You inspect — 2 records exist, only one is &quot;Linked&quot; to active portal access. The other is an orphan from an old invite. Delete the orphan and ask them to log in again.
                  </p>
                  <p className="font-bold text-slate-700 uppercase tracking-wide text-[11px] pt-1">Steps</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-slate-600">
                    <li>Type the parent&apos;s email below and click <strong>Inspect</strong>.</li>
                    <li>Review the table. Rows marked <strong>Linked</strong> (green) are protected — they cannot be deleted.</li>
                    <li>Only <strong>Orphan</strong> rows show a <strong>Delete</strong> button. Click it on the row to remove.</li>
                    <li>Confirm in the dialog. A snapshot is saved to <code>audit_user_deletions</code> before deletion (recoverable).</li>
                    <li>Ask the parent to log out and log back in.</li>
                  </ol>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="parent email (e.g. parent@example.com)"
                    value={cleanupEmail}
                    onChange={e => setCleanupEmail(e.target.value)}
                    data-testid="cleanup-email-input"
                  />
                  <Button onClick={inspectParentUsers} disabled={cleanupLoading} data-testid="cleanup-inspect-btn">
                    {cleanupLoading ? "Searching…" : "Inspect"}
                  </Button>
                </div>

                {cleanupResult && (
                  <div className="space-y-3" data-testid="cleanup-result">
                    <p className="text-sm text-slate-600">
                      <strong>{cleanupResult.userCount}</strong> user record(s) for <code>{cleanupResult.email}</code>;
                      {" "}{cleanupResult.portalAccess.length} portal access row(s).
                    </p>

                    <div className="rounded-lg border border-line overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-left p-2">User ID</th>
                            <th className="text-left p-2">Org</th>
                            <th className="text-left p-2">Role</th>
                            <th className="text-left p-2">Created</th>
                            <th className="text-left p-2">Linked?</th>
                            <th className="text-left p-2">Recommendation</th>
                            <th className="text-left p-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cleanupResult.users.map(u => (
                            <tr key={u.id} className="border-t border-line">
                              <td className="p-2 font-mono">{(u.id || "").slice(0, 8)}…</td>
                              <td className="p-2 font-mono">{(u.organizationId || "").slice(0, 8)}…</td>
                              <td className="p-2">{u.role}</td>
                              <td className="p-2">{(u.createdAt || "").slice(0, 10)}</td>
                              <td className="p-2">
                                {u.isLinkedToPortalAccess
                                  ? <Badge className="bg-emerald-100 text-emerald-700">Linked</Badge>
                                  : <Badge className="bg-slate-100 text-slate-500">Orphan</Badge>}
                              </td>
                              <td className="p-2">{u.recommendation}</td>
                              <td className="p-2">
                                {(u.role || "").toLowerCase() !== "parent" ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">Protected</Badge>
                                ) : u.isLinkedToPortalAccess ? (
                                  <Button size="sm" variant="destructive" className="text-[11px]" onClick={() => setPendingDelete(u)} data-testid={`reset-portal-user-${u.id}`}>
                                    Reset Portal & User
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="destructive" onClick={() => setPendingDelete(u)} data-testid={`delete-user-${u.id}`}>
                                    Delete
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {cleanupResult.userCount === 0 && (
                      <p className="text-sm text-slate-500 italic">No user records match that email.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
              <DialogContent data-testid="confirm-delete-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-rose-700">
                    <AlertTriangle className="w-5 h-5" />
                    {pendingDelete?.isLinkedToPortalAccess ? "Reset Portal Access & Delete User" : "Confirm User Deletion"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-2 text-sm">
                  <p>
                    {pendingDelete?.isLinkedToPortalAccess
                      ? "You're about to delete this parent user AND all their parent_portal_access rows:"
                      : "You're about to permanently delete:"}
                  </p>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 font-mono text-xs space-y-1">
                    <div>email: {cleanupResult?.email}</div>
                    <div>user.id: {pendingDelete?.id}</div>
                    <div>role: {pendingDelete?.role}</div>
                    <div>org: {pendingDelete?.organizationId}</div>
                    <div>created: {pendingDelete?.createdAt}</div>
                  </div>
                  <p className="text-slate-600">
                    A snapshot will be saved to <code>audit_user_deletions</code> before deletion.
                    Owner / staff records and other organizations are <strong>not</strong> affected.
                    {pendingDelete?.isLinkedToPortalAccess && " You can re-send a fresh invite to a different email afterward."}
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPendingDelete(null)} data-testid="cancel-delete-btn">Cancel</Button>
                  <Button variant="destructive" onClick={confirmDeleteUser} data-testid="confirm-delete-btn">
                    {pendingDelete?.isLinkedToPortalAccess ? "Reset & Log" : "Delete & Log"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="pricing">
            <Card className="border border-emerald-200 shadow-lg" data-testid="pricing-manager-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <DollarSign className="h-5 w-5" />
                  Pricing Manager
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Update displayed prices across the app. After saving, update the matching item in your{" "}
                  <a href="https://squareup.com/dashboard/items/library" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-0.5">
                    Square Items & Services <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {pricingLoading && !Object.keys(pricing).length ? (
                  <p className="text-slate-400 text-sm">Loading pricing...</p>
                ) : (
                  <>
                    {/* Group: Subscription Tiers */}
                    {[
                      { group: "Subscription Tiers", keys: ["basic_monthly","basic_annual","premium_monthly","premium_annual","premium_plus_monthly","premium_plus_annual","enterprise_monthly","enterprise_annual"] },
                      { group: "Scale Up Add-Ons", keys: ["scaleup_monthly","scaleup_annual"] },
                      { group: "Revenue Features", keys: ["pickup_safety_monthly","pickup_safety_annual","parent_sms_monthly","parent_sms_annual","family_sms_monthly","family_sms_annual"] },
                    ].map(({ group, keys }) => (
                      <div key={group}>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{group}</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {keys.map(key => {
                            const item = pricing[key] || {};
                            const editVal = pricingEdits[key] ?? item.price ?? "";
                            const changed = parseFloat(editVal) !== item.price;
                            return (
                              <div key={key} className={`rounded-xl border p-3 bg-white transition ${changed ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`}
                                data-testid={`pricing-item-${key}`}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-slate-700 truncate">{item.label}</p>
                                    <p className="text-[10px] text-slate-400 truncate mt-0.5">Square: {item.squareItem}</p>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-xs text-slate-400">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={editVal}
                                      onChange={e => { setPricingEdits(prev => ({ ...prev, [key]: e.target.value })); setPricingDirty(true); }}
                                      className={`w-20 border rounded-lg px-2 py-1 text-sm font-semibold text-right focus:outline-none focus:ring-2 focus:ring-emerald-400 ${changed ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-slate-200"}`}
                                      data-testid={`price-input-${key}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* Save + Square checklist */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-400">Changes shown in green · saved prices update all UI displays immediately</p>
                      <Button onClick={savePricing} disabled={!pricingDirty || pricingLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                        data-testid="save-pricing-btn">
                        {pricingLoading ? "Saving..." : <><Check className="h-4 w-4 mr-1" />Save Prices</>}
                      </Button>
                    </div>

                    {/* Post-save Square reminder */}
                    {savedPricingKeys.length > 0 && (
                      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="square-update-reminder">
                        <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Now update these items in Square
                        </p>
                        <p className="text-xs text-amber-700">Go to Square Dashboard → Items & Services → find each item below → update its price to match:</p>
                        <div className="space-y-1.5">
                          {savedPricingKeys.map(key => pricing[key] && (
                            <div key={key} className="flex items-center justify-between rounded-lg bg-white border border-amber-200 px-3 py-2">
                              <span className="text-xs font-medium text-slate-700">{pricing[key].squareItem}</span>
                              <span className="text-xs font-bold text-emerald-700">${parseFloat(pricingEdits[key] || pricing[key].price).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <a href="https://squareup.com/dashboard/items/library" target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-full transition"
                          data-testid="open-square-dashboard-btn">
                          Open Square Dashboard <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="admin-access">
            <Card className="shadow-card border border-line" data-testid="admin-access-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-red-600" />
                      Admin Support Access Log
                    </CardTitle>
                    <CardDescription>
                      Every time the admin support password is used to sign in, it is recorded here.
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadAdminAccessLog} disabled={adminLogLoading} data-testid="admin-access-refresh">
                    <RefreshCw className={`w-4 h-4 mr-1 ${adminLogLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {adminLogLoading ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-access-loading">Loading…</p>
                ) : adminAccessLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-access-empty">No admin support logins recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-access-table">
                      <thead>
                        <tr className="text-left border-b border-line text-muted-foreground">
                          <th className="py-2 pr-4 font-medium">When (UTC)</th>
                          <th className="py-2 pr-4 font-medium">Account accessed</th>
                          <th className="py-2 pr-4 font-medium">Center</th>
                          <th className="py-2 pr-4 font-medium">IP</th>
                          <th className="py-2 pr-4 font-medium">Device</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminAccessLog.map((e, i) => (
                          <tr key={i} className="border-b border-line/60" data-testid={`admin-access-row-${i}`}>
                            <td className="py-2 pr-4 whitespace-nowrap">{e.timestamp ? new Date(e.timestamp).toISOString().replace('T', ' ').slice(0, 19) : '—'}</td>
                            <td className="py-2 pr-4">{e.accessedEmail || '—'}</td>
                            <td className="py-2 pr-4">{e.organizationId || '—'}</td>
                            <td className="py-2 pr-4">{e.ip || '—'}</td>
                            <td className="py-2 pr-4 max-w-xs truncate" title={e.userAgent}>{e.userAgent || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Staff Tier Assignment Dialog */}

      <Dialog open={staffTierDialogOpen} onOpenChange={setStaffTierDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Test Tier View</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-ink-muted">
              Assign a tier to <strong>{selectedStaff?.name}</strong> to test what customers see at different subscription levels.
            </p>
            <div className="space-y-2">
              <Label>View As Tier</Label>
              <Select value={selectedTier} onValueChange={setSelectedTier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      <span className="flex items-center gap-2">
                        <tier.icon className="w-4 h-4" />
                        {tier.name} - {tier.children}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Test As Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">
                    <span className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-pink-500" />
                      Director (Full Access)
                    </span>
                  </SelectItem>
                  <SelectItem value="co-owner">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-violet-500" />
                      Supervisor/Administrator
                    </span>
                  </SelectItem>
                  <SelectItem value="staff">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      Staff (Limited Access)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <strong>Note:</strong> This staff member will see the app as a {selectedTier?.charAt(0).toUpperCase() + selectedTier?.slice(1)} tier {getRoleDisplayName(selectedRole)} would, including upgrade prompts for locked features.
            </div>
            {selectedStaff?.viewAsTier && (
              <Button 
                variant="outline" 
                className="w-full text-red-500 border-red-200 hover:bg-red-50"
                onClick={async () => {
                  setLoading(true);
                  try {
                    await axios.put(`${API}/owner/clear-staff-tier`, { userId: selectedStaff.id });
                    toast.success(`${selectedStaff.name} restored to full access`);
                    setStaffTierDialogOpen(false);
                    fetchStaffUsers();
                  } catch (error) {
                    toast.error("Failed to clear tier");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                Clear Test Tier (Restore Full Access)
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffTierDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateStaffTier} disabled={loading} className="bg-violet-500 hover:bg-violet-600">
              {loading ? "Saving..." : "Set Test Tier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default OwnerToolsPage;
